import type { ApiPromise } from '@polkadot/api'
import type { BasicSemaphore } from './basicSemaphore'
import { createApiInstance } from './createApiInstance'
import type { ApiCache } from './type'
import type { UrlsSemaphore } from './urlsSemaphore'

const ONE_HOUR = 1000 * 60 * 60 // 1 hour in milliseconds
const ONE_DAY = 24 * ONE_HOUR // 1 day in milliseconds

export const getApiInstance = async (params: {
  cache: ApiCache
  createSemaphore: BasicSemaphore
  urlsSemaphore: UrlsSemaphore
  timeout?: number
}): Promise<{
  api: ApiPromise
  url: string
}> => {
  const { cache, createSemaphore, urlsSemaphore, timeout = ONE_HOUR } = params

  while (true) {
    // Semaphoreを取得して getApiInstance の同時実行を制限
    await createSemaphore.acquire()

    try {
      if (cache.api && cache.url) {
        return {
          api: cache.api,
          url: cache.url,
        }
      }

      // すべてのURLがcooldown中の場合、エラーを投げる
      if (urlsSemaphore.isAllUrlsInCooldown()) {
        throw new Error('すべての WS URLがcooldown中です')
      }

      let url: string
      try {
        url = await urlsSemaphore.acquire(timeout)
      } catch (error) {
        throw new Error(
          `利用可能な WS URLが取得できませんでした: ${error instanceof Error ? error.message : String(error)}`,
        )
      }

      try {
        // 取得したURLを使用してapiPromiseを作成
        const api = await createApiInstance({ url })

        // apiPromiseをキャッシュに保存
        cache.url = url
        cache.api = api

        return {
          api,
          url,
        }
      } catch (error) {
        // Timeoutエラーの場合、cooldownをONE_HOURに設定して再試行
        if (error instanceof Error && error.message.includes('Timeout')) {
          urlsSemaphore.release(url, ONE_HOUR)
          continue
        }

        // それ以外のエラーは cooldown を ONE_DAY に設定して再試行
        urlsSemaphore.release(url, ONE_DAY)
      }
    } finally {
      // セマフォを解放
      createSemaphore.release()
    }
  }
}
